import { createFileRoute } from "@tanstack/react-router";
import campingImg from "@/assets/camping.webp";
import omOss1 from "@/assets/om-oss-1.avif.asset.json";
import omOss2 from "@/assets/om-oss-2.avif.asset.json";
import { PageHeader } from "./fordon";

export const Route = createFileRoute("/om-oss")({
  head: () => ({
    meta: [
      { title: "Om oss – Familjeägd husbilshandlare i Kalix sedan 2004" },
      {
        name: "description",
        content:
          "Lär känna AK Husbil & Fritid — familjeföretaget i Kalix som lever för fritidslivet. 20 års erfarenhet, ärliga råd och personlig service.",
      },
      { property: "og:title", content: "Om oss – AK Husbil & Fritid" },
      {
        property: "og:description",
        content: "Familjeägt sedan 2004 i Kalix. Du pratar alltid med samma personer som tar hand om fordonen.",
      },
      { property: "og:url", content: "https://www.akhusbilfritid.se/om-oss" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/om-oss" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "Om AK Husbil & Fritid",
          url: "/om-oss",
          mainEntity: { "@id": "#organization" },
        }),
      },
    ],
  }),
  component: OmOssPage,
});

const facts = [
  { k: "Sedan", v: "2004 i Kalix" },
  { k: "Specialitet", v: "Husbilar & husvagnar" },
  { k: "Löfte", v: "Kontrollerade fordon" },
  { k: "Service", v: "Du pratar med ägaren" },
];

function OmOssPage() {
  return (
    <>
      <PageHeader
        label="Familjeföretag · Sedan 2004"
        title={<>Människorna bakom <span className="font-italic-display text-accent">affären</span>.</>}
        sub="Vi är ett litet familjeföretag i Kalix som lever för fritidslivet. När du ringer svarar samma person som tar hand om fordonet — så har vi byggt vårt rykte i 20 år."
      />

      <section className="container-edge py-20 md:py-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-7 md:pr-6">
            <p className="text-xl leading-relaxed text-foreground/90 md:text-2xl">
              Vi tror att en bra affär börjar med ärlighet. Därför säljer vi
              bara fordon vi själva skulle köra hem — och berättar både det
              bra och det som behöver göras.
            </p>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
              Inga säljscript, ingen press. Du får tid att titta, prata och
              tänka. Och när du köpt fordonet finns vi kvar — med service,
              tillbehör och svar på frågor långt efter att nycklarna bytt händer.
            </p>

            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              {facts.map((f) => (
                <div key={f.k} className="rounded-2xl border border-border bg-card p-5">
                  <p className="text-xs font-medium uppercase tracking-wider text-accent">{f.k}</p>
                  <p className="mt-2 font-display text-xl">{f.v}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="img-frame aspect-[4/5] bg-card">
              <img src={campingImg} alt="Camping vid sjön" loading="lazy" className="h-full w-full object-cover" />
            </div>
          </div>
        </div>

        <div className="mt-20">
          <p className="eyebrow">Från våra äventyr</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Fritidslivet vi <span className="font-italic-display text-accent">lever</span> för.
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Vi säljer inte bara fordon — vi använder dem själva. Här är några ögonblick från vägarna, campingplatserna och stunderna som gör det värt varje mil.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-8 sm:gap-10">
            {[omOss1, omOss2].map((img, i) => (
              <figure key={i} className="gold-frame w-full max-w-[280px] sm:max-w-[320px]">
                <img
                  src={img.url}
                  alt="AK Husbil & Fritid – ögonblick från fritidslivet"
                  loading="lazy"
                  className="block h-auto w-full object-cover"
                />
              </figure>
            ))}
          </div>
        </div>

        {/* PARTNER SECTION — Fyndanu */}
        <div className="mt-20 overflow-hidden rounded-3xl border border-border bg-foreground text-background">
          <div className="relative p-8 md:p-12">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-60"
              style={{
                background:
                  "radial-gradient(70% 90% at 15% 30%, oklch(0.45 0.14 38 / 0.35), transparent 65%)",
              }}
            />
            <div className="relative grid gap-8 md:grid-cols-12 md:items-center">
              <div className="text-center md:col-span-7 md:text-left">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-accent">
                  Samarbetspartner
                </p>
                <h3 className="mt-4 font-display text-3xl md:text-5xl">
                  Vi tror på transparens — därför{" "}
                  <span className="font-italic-display headline-accent">Fyndanu</span>.
                </h3>
                <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-background/75 md:mx-0">
                  Vi valde tidigt att bli officiell butik på Fyndanu.se — en
                  svensk marknadsplats byggd för att göra köp och sälj av
                  fritidsfordon rakare och tryggare. Där finns hela vårt
                  lager, alltid uppdaterat, alltid med öppen prislapp. Samma
                  filosofi som styrt oss sedan 2004.
                </p>
                <a
                  href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-cta btn-cta-accent group mx-auto mt-7 inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold md:mx-0"
                >
                  Besök vår Fyndanu-butik →
                </a>
              </div>
              <div className="md:col-span-5">
                <div className="rounded-2xl border border-background/15 bg-background/[0.04] p-6 text-center backdrop-blur-sm">
                  <p className="font-display text-5xl leading-none tracking-tight text-background md:text-6xl">
                    fyndanu<span className="text-accent">.</span>se
                  </p>
                  <p className="mt-3 text-xs uppercase tracking-[0.22em] text-background/60">
                    Verifierad butik · Sedan lansering
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>


        <div className="mt-20">
          <p className="eyebrow">Kontaktpersoner</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Prata med <span className="font-italic-display text-accent">oss</span>.
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-xs font-medium uppercase tracking-wider text-accent">Säljare</p>
              <p className="mt-2 font-display text-2xl">Aaron</p>
              <a href="tel:0708629743" className="mt-3 inline-flex items-center gap-2 text-base text-foreground hover:text-accent">
                070-862 97 43
              </a>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-xs font-medium uppercase tracking-wider text-accent">Inköp</p>
              <p className="mt-2 font-display text-2xl">Vi köper din husbil</p>
              <a href="tel:0703045344" className="mt-3 inline-flex items-center gap-2 text-base text-foreground hover:text-accent">
                070-304 53 44
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

